<script>
import baseMixin from '@/mixins/baseMixin'
import { mapGetters, mapActions } from 'vuex'
import { searchItem, highlightText } from '@/helpers/search-helpers'

import CommentType from '@/constants/comments/CommentType'
import DisputesDataTableCellKeys from '@/constants/disputes/DisputesDataTableCellKeys'
import ClientDetailsListItem from '../general/client-details-list-item.vue'
import DisputesTableBookingDateV2 from './disputes-table-booking-date-v2.vue'
import DisputesTableSubmittedIndicator from './disputes-table-submitted-indicator.vue'
import DisputesTableBreaks from './disputes-table-breaks.vue'
import DisputesTableRestBreaksIndicator from './disputes-table-rest-breaks-indicator.vue'

const {
  BOOKING_ID,
  LOCATION_NAME,
  CANDIDATE_NAME,
  BREAK_MINUTES,
  TASK_TYPE,
  BOOKING_DATE_TIME,
  ASSIGNED_TO,
  CANDIDATE_HAS_SUBMITTED,
  CLIENT_HAS_SUBMITTED,
  IS_REST_BREAK_COMPLIANT,
} = DisputesDataTableCellKeys

export default {
  components: {
    ClientDetailsListItem,
    DisputesTableBookingDateV2,
    DisputesTableSubmittedIndicator,
    DisputesTableBreaks,
    DisputesTableRestBreaksIndicator,
  },
  mixins: [baseMixin],
  props: {
    autocompleteSelectedModel: {
      type: Object,
      default: () => null,
    },
    search: {
      type: String,
      default: '',
    },
    sortStrategy: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    itemsPerPageOptions: [10, 15, 20, 50],
  }),
  computed: {
    ...mapGetters({
      getUserNameById: 'users/getUserNameById',
      disputeTasks: 'disputes/disputeFollowUpTasks',
      isLoadingDisputeTasks: 'disputes/isLoadingDisputeTasks',
    }),
    showRestBreaksColumn() {
      return Boolean(
        this.disputeTasks.find(
          (disputeTask) => disputeTask?.isRestBreakCompliant !== null
        )
      )
    },
    filters() {
      return {
        filter: this.autocompleteSelectedModel,
      }
    },
    baseTableHeaders() {
      return [
        {
          text: this.$t('dispute.table.columnHeadings.bookingId'),
          align: 'start',
          value: BOOKING_ID,
        },
        {
          text: this.$t('dispute.table.columnHeadings.locationDetails'),
          value: LOCATION_NAME,
        },
        {
          text: this.$t('dispute.table.columnHeadings.candidate'),
          value: CANDIDATE_NAME,
        },
        {
          text: this.$t('dispute.table.columnHeadings.bookingTime'),
          value: BOOKING_DATE_TIME,
        },
        {
          text: this.$t('dispute.table.columnHeadings.breaks'),
          value: BREAK_MINUTES,
        },
        {
          text: this.$t('dispute.table.columnHeadings.taskType'),
          value: TASK_TYPE,
        },
        {
          text: this.$t('dispute.table.columnHeadings.restBreaks'),
          value: IS_REST_BREAK_COMPLIANT,
          display: this.showRestBreaksColumn,
          align: 'center',
        },
        {
          text: this.$t('dispute.table.columnHeadings.candidateSubmittedShort'),
          value: CANDIDATE_HAS_SUBMITTED,
          align: 'center',
        },
        {
          text: this.$t('dispute.table.columnHeadings.clientSubmittedShort'),
          value: CLIENT_HAS_SUBMITTED,
          align: 'center',
        },
        {
          text: this.$t('dispute.table.columnHeadings.assignedTo'),
          value: ASSIGNED_TO,
          sortable: false,
        },
        {
          text: '',
          value: 'actions',
          align: 'end',
          sortable: false,
        },
      ]
    },
    tableHeaders() {
      return this.baseTableHeaders.filter(
        (header) => !Object.hasOwn(header, 'display') || header?.display
      )
    },
  },
  methods: {
    ...mapActions({
      getDisputeFollowUpTasks: 'disputes/getDisputeFollowUpTasks',
    }),
    highlightText,
    searchTable() {
      return (_value, q, item) =>
        searchItem(item, q, [
          this.disputesSearchTaskTypePreprocessor,
          this.disputesSearchAssignedToPreprocessor,
        ])
    },
    customSort(items, sortBy, sortDesc) {
      const strategy = this.sortStrategy
      if (!strategy) return items
      return strategy.sort(items, sortBy, sortDesc)
    },
    disputesSearchTaskTypePreprocessor(item) {
      return this.taskDescriptionFromType(item.taskType).text
    },
    disputesSearchAssignedToPreprocessor(item) {
      return this.getUserNameById(item.assignedTo)
    },
    taskDescriptionFromType(taskType) {
      let description = null

      switch (taskType) {
        case CommentType.DisputeFollowUpCandidateExtendedShiftDuration:
          description = {
            text: 'Candidate extended shift duration',
            color: 'warning',
          }
          break
        case CommentType.DisputeFollowUpCandidateReducedMealBreak:
          description = {
            text: 'Candidate reduced meal break',
            color: 'warning',
          }
          break
        case CommentType.DisputeFollowUpCandidateStatedDidNotHappen:
          description = { text: "Client stated didn't happen", color: 'red' }
          break
        default:
          description = { text: 'Unknown', color: 'grey' }
          break
      }

      description.text = highlightText(description.text, this.search)

      return description
    },
    hasRestBreakCompliantValue(item) {
      return (
        item?.isRestBreakCompliant !== null &&
        typeof item?.isRestBreakCompliant !== 'undefined'
      )
    },
    hasSubmittedTimesheet(submittedDate) {
      return Boolean(submittedDate)
    },
    mockedSubmittedTimesheetObject(date) {
      return {
        submittedDateUtc: date,
      }
    },
    /**
     * The table expects a function to return the item class.
     */
    getTableItemClass() {
      return 'cursor-pointer'
    },
    openTaskResolveForm(item) {
      this.$emit('openTask', {
        bookingId: item.bookingId,
        taskId: item.taskId,
      })
    },
    getAssigneeName(adminId) {
      return this.getUserNameById(adminId)
    },
  },
}
</script>

<template>
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="disputeTasks"
      :loading="isLoadingDisputeTasks"
      class="pt-4"
      :item-class="getTableItemClass"
      :search="search"
      :custom-filter="searchTable"
      :footer-props="{
        itemsPerPageOptions,
      }"
      multi-sort
      :custom-sort="customSort"
      @click:row="openTaskResolveForm"
    >
      <template v-slot:item.bookingId="{ item }">
        <div>
          <BaseLink
            :href="returnBookingUrl(item.bookingId)"
            color="primary"
            :title="$t('general.link.viewBookingTooltip')"
            allow-insecure
            ><span
              v-sanitized-html="highlightText(item.bookingId, search)"
            ></span
          ></BaseLink>
        </div>
      </template>
      <template v-slot:item.locationName="{ item }">
        <ClientDetailsListItem
          :location-id="item.locationId"
          :location-title="item.locationTitle"
          :search="search"
        />
      </template>
      <template v-slot:item.candidateName="{ item }">
        <div>
          <BaseLink
            :href="returnCandidateUrl(item.candidateId)"
            color="primary"
            title="View candidate in relief roster"
            allow-insecure
            ><span
              v-sanitized-html="highlightText(item.candidateName, search)"
            ></span
          ></BaseLink>
        </div>
      </template>

      <template v-slot:item.breakMinutes="{ item }">
        <DisputesTableBreaks :dispute="item" />
      </template>

      <template v-slot:item.taskType="{ item }">
        <v-chip small :color="isDarkMode ? '' : 'grey lighten-4'">
          <v-badge
            inline
            left
            dot
            :color="taskDescriptionFromType(item.taskType).color"
            class="ml-n1"
            offset-x="2px"
            ><div
              v-sanitized-html="taskDescriptionFromType(item.taskType).text"
              class="ml-1"
            ></div
          ></v-badge>
        </v-chip>
      </template>

      <template v-slot:item.bookingDateTime="{ item }">
        <DisputesTableBookingDateV2 :dispute="item" />
      </template>

      <template v-slot:item.candidateHasSubmitted="{ item }">
        <div class="d-flex align-center justify-center">
          <DisputesTableSubmittedIndicator
            v-if="hasSubmittedTimesheet(item.candidateSubmittedDate)"
            class="w-fit-content"
            :timesheet="
              mockedSubmittedTimesheetObject(item.candidateSubmittedDate)
            "
          />
          <span v-else>{{ $t('general.emptyCell') }}</span>
        </div>
      </template>
      <template v-slot:item.clientHasSubmitted="{ item }">
        <div class="d-flex align-center justify-center">
          <DisputesTableSubmittedIndicator
            v-if="hasSubmittedTimesheet(item.clientSubmittedDate)"
            class="w-fit-content"
            :timesheet="
              mockedSubmittedTimesheetObject(item.clientSubmittedDate)
            "
          />
          <span v-else>{{ $t('general.emptyCell') }}</span>
        </div>
      </template>

      <template v-slot:item.isRestBreakCompliant="{ item }">
        <div class="d-flex align-center justify-center">
          <DisputesTableRestBreaksIndicator
            v-if="hasRestBreakCompliantValue(item)"
            class="w-fit-content"
            :compliant="item.isRestBreakCompliant"
          />
        </div>
      </template>

      <template v-slot:item.assignedTo="{ item }">
        {{ getAssigneeName(item.assignedTo) }}
      </template>

      <template v-slot:item.actions>
        <v-icon class="opacity-6">mdi-chevron-right</v-icon>
      </template>
    </v-data-table>
  </div>
</template>
